import React from 'react';
import { Row, Col, Card, Typography } from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import RouterProvider from '@pankod/refine-react-router-v6';
import {
  RiBarcodeBoxLine,
  RiStore3Fill,
  RiShoppingBag2Line,
  RiRefundLine,
  RiDeleteBin2Line,
  RiTodoLine,
  RiArrowRightLine,
} from 'react-icons/ri';
import './dashboard-cards.css';

const { Text, Title } = Typography;
const { Link } = RouterProvider;

export interface PosModuleItem {
  key: string;
  title: string;
  description: string;
  route: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
}

export const posModules: PosModuleItem[] = [
  {
    key: 'pos',
    title: 'POS',
    description: 'Start a new sale, scan or search products, checkout.',
    route: '/pos',
    icon: <RiBarcodeBoxLine size={32} />,
    color: '#0284c7',
    bgColor: '#e0f2fe',
    borderColor: '#bae6fd',
  },
  {
    key: 'sales',
    title: 'Sales',
    description: 'View sales list and history for the store.',
    route: '/sales',
    icon: <RiStore3Fill size={32} />,
    color: '#059669',
    bgColor: '#d1fae5',
    borderColor: '#a7f3d0',
  },
  {
    key: 'purchases',
    title: 'Purchase',
    description: 'Log incoming stock from suppliers.',
    route: '/purchases',
    icon: <RiShoppingBag2Line size={32} />,
    color: '#7c3aed',
    bgColor: '#ede9fe',
    borderColor: '#ddd6fe',
  },
  {
    key: 'returns',
    title: 'Return',
    description: 'Customer and supplier returns.',
    route: '/returns',
    icon: <RiRefundLine size={32} />,
    color: '#d97706',
    bgColor: '#fef3c7',
    borderColor: '#fde68a',
  },
  {
    key: 'damages',
    title: 'Damage',
    description: 'Log damaged or expired stock.',
    route: '/damages',
    icon: <RiDeleteBin2Line size={32} />,
    color: '#dc2626',
    bgColor: '#fee2e2',
    borderColor: '#fca5a5',
  },
  {
    key: 'inventories',
    title: 'Inventory',
    description: 'Check current stock and low stock alerts.',
    route: '/inventories',
    icon: <RiTodoLine size={32} />,
    color: '#0d9488',
    bgColor: '#ccfbf1',
    borderColor: '#99f6e4',
  },
];

const PosNavCard: React.FC<{ module: PosModuleItem }> = ({ module }) => {
  // Same accessControlProvider check the sidebar uses to decide whether a resource's
  // list page shows up at all - keeps this dashboard shortcut consistent with the menu.
  const { data: canAccess } = useCan({ resource: module.key, action: 'list' });

  if (!canAccess?.can) {
    return null;
  }

  return (
    <Col xl={8} lg={8} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
      <Link to={module.route} style={{ textDecoration: 'none', display: 'flex', width: '100%' }}>
        <Card
          className="pos-nav-card"
          hoverable
          style={{
            borderRadius: '12px',
            border: `1px solid ${module.borderColor}`,
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
            transition: 'all 0.25s ease-in-out',
            width: '100%',
            minHeight: '116px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
          bodyStyle={{
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
            minHeight: '116px',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="pos-nav-icon-badge"
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              backgroundColor: module.bgColor,
              color: module.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'transform 0.25s ease-in-out',
            }}
          >
            {module.icon}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '4px',
              }}
            >
              <Title
                level={5}
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#0f172a',
                }}
              >
                {module.title}
              </Title>
              <RiArrowRightLine
                className="pos-nav-arrow"
                size={20}
                style={{
                  color: module.color,
                  opacity: 0.6,
                  transition: 'all 0.25s ease-in-out',
                }}
              />
            </div>
            <Text
              style={{
                fontSize: '13px',
                color: '#64748b',
                lineHeight: '1.4',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {module.description}
            </Text>
          </div>
        </Card>
      </Link>
    </Col>
  );
};

export const PosNavigationMenu: React.FC = () => {
  return (
    <div className="pos-nav-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ marginBottom: '4px', color: '#1e293b', fontWeight: 600 }}>
          POS Quick Navigation
        </Title>
        <Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
          Quick access to primary point-of-sale operational modules
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        {posModules.map((module) => (
          <PosNavCard key={module.key} module={module} />
        ))}
      </Row>
    </div>
  );
};
